/**
 * @module components/CardBacks
 * @description The printed back of each story postcard, per edition:
 *
 * - Gala → `PledgeCardBack`: the paper pledge card ("Turn over to give"),
 *   built from the same edition config as the web pledge form, so paper and
 *   digital pledges ask the same questions in the same words.
 * - Campaign → `MailBack`: a mailable postcard back — message side with a
 *   per-card QR code (story + giving page, with ?from= attribution), and an
 *   address side with a stamp box.
 *
 * `MailingPanel` (stamp box, script line, address lines) is shared with the
 * thank-you postcard back.
 */
import { forwardRef } from "react";
import type { StoryCard } from "../domain/types";
import type { EditionConfig } from "../config/editions";
import { CAMPAIGN, CAUSE_LABELS, formatUsd } from "../config/campaign";
import { absoluteUrlFor } from "../app/router";
import { useEdition } from "../app/edition";
import { useCardOrientation } from "../app/cardOrientation";
import { orientationClass } from "./StoryPostcard";
import { BrandLockup } from "./Brand";
import { QrCode } from "./QrCode";

/** Legal line printed on every back. */
export function LegalLine() {
    return (
        <p className="cb-legal">
            {CAMPAIGN.organizationName} is a 501(c)(3) nonprofit, EIN {CAMPAIGN.ein}. Gifts are tax-deductible to the extent allowed by law.
        </p>
    );
}

/** Address side: stamp box, script line, recipient lines (typed, or blank for hand addressing). */
export function MailingPanel({ script, recipient = "" }: { script: string; recipient?: string }) {
    const typed = recipient.split("\n").filter((line) => line.trim());
    const lines = Array.from({ length: 4 }, (_, i) => typed[i] ?? "");
    return (
        <div className="mail-panel">
            <div className="mail-stamp-box">
                <span>Place<br />stamp<br />here</span>
            </div>
            <p className="pc-script mail-script">{script}</p>
            <ol className="mail-lines" aria-label="Address">
                {lines.map((line, i) => (
                    <li key={i} className="hand-ink">{line}</li>
                ))}
            </ol>
        </div>
    );
}

/** An empty checkbox square for paper forms. */
const Box = () => <span className="pb-box" aria-hidden="true" />;

/** Write-in line with a label. */
const Line = ({ label, grow = 1 }: { label: string; grow?: number }) => (
    <span className="pb-line" style={{ flexGrow: grow }}>
        <span>{label}</span>
        <i aria-hidden="true" />
    </span>
);

export interface CardBackProps {
    card: StoryCard;
    edition?: EditionConfig;
}

/** Gala edition: the paper pledge card. */
export const PledgeCardBack = forwardRef<HTMLElement, CardBackProps>(function PledgeCardBack({ card, edition }, ref) {
    const active = useEdition();
    const ed = edition ?? active;
    const orientation = useCardOrientation();
    const giveUrl = absoluteUrlFor({ name: "pledge", fromSlug: card.slug });
    return (
        <article ref={ref} className={`postcard postcard--back pb ${orientationClass(orientation)}`} aria-label={`Pledge card (back of ${card.title})`}>
            <aside className="pb-aside">
                <BrandLockup variant="vertical" className="pb-lockup" />
                <p className="pb-to">To Jamaica,</p>
                <p className="pc-script pb-script">With Love.</p>
                <hr className="pc-rule pc-rule--short" />
                <p className="pb-cap">{ed.pledge.asideHeadline}</p>
                {ed.pledge.asideKicker && <p className="pb-kicker">{ed.pledge.asideKicker}</p>}
                <p className="pb-lede">{ed.pledge.asideLede}</p>
                <p className="pb-tagline">{ed.pledge.asideTagline}</p>
                <div className="pb-qr">
                    <QrCode value={giveUrl} className="pb-qr-code" label={`QR code: give online, from ${card.title}`} />
                    <span>Scan to give now</span>
                </div>
            </aside>
            <div className="pb-form">
                <section>
                    <h3 className="pb-legend"><b>1</b> {ed.pledge.amountLegend}</h3>
                    <div className="pb-amounts">
                        {ed.pledge.presetAmounts.map((amount) => (
                            <span key={amount} className="pb-opt"><Box />{formatUsd(amount)}</span>
                        ))}
                        <span className="pb-opt pb-opt--other"><Box /><Line label="Other $" /></span>
                    </div>
                    <span className="pb-opt"><Box /><span><b>Make it monthly</b> and give all year long.</span></span>
                </section>
                <section>
                    <h3 className="pb-legend"><b>2</b> How I'll give</h3>
                    <div className="pb-grid2">
                        {ed.pledge.intents.map(({ intent, label }) => (
                            <span key={intent} className="pb-opt"><Box />{label}</span>
                        ))}
                    </div>
                </section>
                <section>
                    <h3 className="pb-legend"><b>3</b> My details</h3>
                    <div className="pb-row"><Line label="Name" /></div>
                    <div className="pb-row"><Line label="Email" grow={1.6} /><Line label="Phone" /></div>
                    <div className="pb-row">
                        <span className="pb-opt"><Box />Please keep my gift anonymous</span>
                        {ed.pledge.showTableNumber && <Line label="Table" grow={0.4} />}
                    </div>
                </section>
                <section>
                    <h3 className="pb-legend"><b>4</b> What's closest to your heart?</h3>
                    <div className="pb-causes">
                        {Object.values(CAUSE_LABELS).map((label) => (
                            <span key={label} className="pb-opt"><Box />{label}</span>
                        ))}
                    </div>
                    <span className="pb-opt"><Box /><b>I'd love to talk with AFJ about a larger or multi-year gift.</b></span>
                </section>
                <LegalLine />
            </div>
        </article>
    );
});

/** Campaign edition: a mailable postcard back with a per-card QR code. */
export const MailBack = forwardRef<HTMLElement, CardBackProps>(function MailBack({ card }, ref) {
    const orientation = useCardOrientation();
    const storyUrl = absoluteUrlFor({ name: "card", slug: card.slug });
    return (
        <article ref={ref} className={`postcard postcard--back mb ${orientationClass(orientation)}`} aria-label={`Postcard back: ${card.title}`}>
            <div className="mb-message">
                <p className="pc-kicker mb-kicker">Postcard from Jamaica · {card.title}</p>
                <p className="mb-headline">Put your love for Jamaica to work.</p>
                <p className="mb-body">
                    Every gift made through {CAMPAIGN.campaignName} goes where Jamaica needs it most, from the next school roof to a
                    child's first eye exam.
                </p>
                <div className="mb-qr">
                    <QrCode value={storyUrl} className="mb-qr-code" label={`QR code: read the ${card.title} story and give`} />
                    <p>
                        <b>Scan to read this story and give.</b>
                        <br />
                        Or visit {CAMPAIGN.siteLabel}
                    </p>
                </div>
                {/* Blank ruled lines for a personal note when the card is mailed by hand. */}
                <div className="mb-note" aria-hidden="true"><i /><i /><i /></div>
                <div className="mb-sign">
                    <BrandLockup variant="horizontal" className="mb-lockup" />
                    <LegalLine />
                </div>
            </div>
            <span className="mb-divider" aria-hidden="true" />
            <MailingPanel script={`${CAMPAIGN.campaignName}`} />
        </article>
    );
});

/** The back the active edition prints behind a story card. */
export const StoryCardBack = forwardRef<HTMLElement, CardBackProps>(function StoryCardBack({ card, edition }, ref) {
    const active = useEdition();
    const ed = edition ?? active;
    return ed.storyCardBack === "pledge" ? <PledgeCardBack ref={ref} card={card} edition={ed} /> : <MailBack ref={ref} card={card} />;
});
